(ns manage.ad-variant-preview.hooks
  (:require ["react-use/lib/useKey$default" :as useKey]
            [manage.ad-variant-preview.data :as data]
            [manage.ad-variant-preview.db :as db]
            [manage.ad-variant-preview.viewport :as viewport]
            [manage.ad-variant.data :as variant-data]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(defhook use-video-controls [{:keys [video-controls video-status]}]
  (let [{:keys [event-target set-video-controls]} (viewport/use-context)

        {:keys [auto-replay timeline-selection-editing start end audio-active-variant]} video-controls

        ;; Prefer video element with the longest duration as the "master",
        ;; this is used for the timeline slider duration & playback status/position.
        {:keys [max-duration video]}
        (uix/use-memo (fn []
                        (data/find-longest-video video-status))
                      ;; NOTE: Each Clj map change changes JS Object identity, retrigger this.
                      [video-status])

        {:keys [status el]} video
        playing (= "playing" status)
        ;; This flag tells if the play event should restart videos.
        ;; Videos should go back to the start only once all videos (the longest video)
        ;; is also at the end.
        all-end (= "end" status)

        [progress set-progress] (uix/use-state 0)

        toggle-play (uix/use-callback (fn []
                                        (set-video-controls #(assoc % :timeline-selection-editing false))
                                        (viewport/dispatch event-target (if playing "pause" "play")
                                                           {:all-end all-end}))
                                      ;; event-target is stable
                                      ^:lint/disable
                                      [playing all-end])

        toggle-auto-replay (uix/use-callback (fn []
                                               (set-video-controls #(update % :auto-replay not)))
                                             ;; set-video-controls is stable
                                             ^:lint/disable
                                             [])

        toggle-timeline-selection-editing (uix/use-callback (fn []
                                                              (set-video-controls #(update % :timeline-selection-editing not))
                                                              (viewport/dispatch event-target "pause"))
                                                            ;; event-target is stable
                                                            ^:lint/disable
                                                            [])

        set-timeline-selection (uix/use-callback (fn [start end]
                                                   (set-video-controls #(assoc % :start start :end end)))
                                                 ;; set-video-controls is stable
                                                 ^:lint/disable
                                                 [])

        set-timeline-progress (uix/use-callback (fn [target opts]
                                                  (let [start (or start 0)
                                                        end (or end max-duration)
                                                        bounded-target (min end (max start target))]
                                                    (viewport/dispatch event-target "seek" (merge {:target bounded-target} opts))
                                                    (set-progress bounded-target)))
                                                ;; event-target is stable
                                                ^:lint/disable
                                                [start end max-duration])

        restore-timeline-progress (uix/use-callback (fn []
                                                      (viewport/dispatch event-target "seek" {:restore-position true})
                                                      ;; Update the slider value to the current timestamp
                                                      (when el
                                                        (set-progress (.-currentTime el))))
                                                    ;; event-target is stable
                                                    ^:lint/disable
                                                    [el])

        mute-all-videos (uix/use-callback (fn []
                                            (set-video-controls #(assoc % :audio-active-variant nil)))
                                          ;; set-video-controls is stable
                                          ^:lint/disable
                                          [])

        toggle-audio-for-variant (uix/use-callback (fn [uuid]
                                                     (let [audio-active (= uuid audio-active-variant)]
                                                       (set-video-controls (if audio-active
                                                                             #(assoc % :audio-active-variant nil)
                                                                             #(assoc % :audio-active-variant uuid)))))
                                                   ;; set-video-controls is stable
                                                   ^:lint/disable
                                                   [audio-active-variant])]

    ;; Create smooth progressbar - update progress value every 16ms
    (uix/use-effect (fn []
                      (when el
                        (let [done (atom false)
                              f (fn f []
                                  (let [t (.-currentTime el)]
                                    ;; Set progress exactly to the end of selection so the slider doesn't go over,
                                    ;; even if video plays for a few more frames.
                                    (set-progress (if (and end (>= t end))
                                                    end
                                                    t)))
                                  (when-not @done
                                    (.requestAnimationFrame js/window f)))]
                          (f)
                          (fn []
                            (reset! done true)))))
                    [el end])

    ;; Trigger auto-replay when all videos are ready
    (uix/use-effect (fn []
                      (when (and auto-replay (= "end" status))
                        ;; Seek to start of the selection or the video.
                        (viewport/dispatch event-target "seek" {:target (or start 0)})
                        (viewport/dispatch event-target "play" {:all-end true})))
                    ;; Triggered event time a new Clj map is created. This is OK,
                    ;; because map identity is changed when video element is mounted or status changes.
                    ;; event-target is stable
                    ^:lint/disable
                    [auto-replay status start])

    ;; Toggle play using space key
    (useKey (fn [e]
              (let [target-tag (.toLowerCase (.-tagName (.-target e)))]
                (if (and (not (or (= target-tag "input")
                                  (= target-tag "textarea")))
                         (= (.-key e) " "))
                  (do (.preventDefault e)
                      true)
                  false)))
            toggle-play
            #js {}
            #js [toggle-play])

    {:auto-replay auto-replay
     :toggle-auto-replay toggle-auto-replay

     :playing playing
     :toggle-play toggle-play

     :timeline-disabled (nil? el)
     :timeline-selection [start end]
     :timeline-progress progress
     :timeline-selection-editing timeline-selection-editing

     :set-timeline-selection set-timeline-selection
     :set-timeline-progress set-timeline-progress
     :restore-timeline-progress restore-timeline-progress
     :toggle-timeline-selection-editing toggle-timeline-selection-editing

     :mute-all-videos mute-all-videos
     :audio-active-variant audio-active-variant
     :toggle-audio-for-variant toggle-audio-for-variant
     :max-duration max-duration}))

(defhook use-filter-options [{:keys [variants review-mode? user-is-reviewer?]}]
  (let [ad-types (uix/use-memo
                  (fn []
                    (->> variants
                         (map :ad-type)
                         (into #{})))
                  [variants])
        display-resolutions (uix/use-memo
                             (fn []
                               (->> variants
                                    (map :dimensions)
                                    (into #{})))
                             [variants])
        options (uix/use-memo
                 (fn []
                   (->> data/status-filters
                        (filter (fn [{:keys [visible-for]}]
                                  (if (and user-is-reviewer? review-mode?)
                                    (visible-for :reviewer)
                                    (visible-for :non-reviewer))))
                        (concat (when (seq (rest ad-types))
                                  (map (fn [ad-type]
                                         {:type :ad-type
                                          :value ad-type})
                                       (sort ad-types))))
                        (concat (when (seq (rest display-resolutions))
                                  (map (fn [x]
                                         {:type :resolution
                                          :value x})
                                       (sort-by (juxt :width :height) display-resolutions))))
                        (map (fn [{:keys [type value] :as option}]
                               (assoc option :title (data/get-filter-value-title type value))))))
                 [ad-types display-resolutions review-mode? user-is-reviewer?])]
    options))

(defhook use-fetch-and-select-all-ad-variants [ad-variants-response]
  (let [{:keys [isLoading isFetchingNextPage fetchNextPage hasNextPage]
         {:keys [pages]} :data} ad-variants-response

        all-variants (uix/use-memo (fn []
                                     (map :data (mapcat :data pages)))
                                   [pages])

        visible-selection (use-subscribe [::db/visible])

        [done-loading? set-done-loading?] (uix/use-state false)
        made-visible (uix/use-ref false)]
    (uix/use-effect
     (fn []
       ;; Wait till first fetch has completed, only then we can determine
       ;; `hasNextPage` reliably.
       (when (not isLoading)
         (when (not isFetchingNextPage)
           (if hasNextPage
             (fetchNextPage)
             (set-done-loading? true)))))
     [isLoading isFetchingNextPage hasNextPage fetchNextPage])

    ;; Select the variants in the first response
    ;; unless there already is a selection.
    (uix/use-effect
     (fn []
       (when (and (seq all-variants) (not @made-visible))
         (when (empty? visible-selection)
           (rf/dispatch [::db/set-visible (variant-data/variants->selection all-variants)]))
         (reset! made-visible true)))
     [ad-variants-response all-variants visible-selection])

    {:is-fetching-variants (not done-loading?)
     :all-variants all-variants}))

(defhook use-variant-error [variant]
  (let [{:keys [components ad-type]} variant
        {:keys [error]} (variant-data/get-main-component components ad-type)]
    error))

(defhook use-product-overrides
  [{:keys [variant product-overrides product-selector-id
           advertiser-id shared-product-overrides]}]
  (let [products-overrides-vec (get product-overrides product-selector-id)
        shared-products (get shared-product-overrides (keyword product-selector-id))
        overwritten-product-ids (mapv :id products-overrides-vec)
        products-for-passthrough (if shared-products
                                   {:products [{:placeholder "products"
                                                :product-ids shared-products}]}
                                   (when (seq overwritten-product-ids)
                                     {:products [{:placeholder "products"
                                                  :product-ids overwritten-product-ids}]}))
        preview-passthrough (merge {:ad-variant-uuid (:uuid variant)
                                    :advertiser-id advertiser-id}
                                   products-for-passthrough)]
    {:preview-passthrough preview-passthrough}))
